import React, { useEffect, useState, useMemo } from 'react';
import { Table } from 'antd';
import { FormField } from 'components';
import { debounce, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { isBeneficiary } from 'utils';
import { supplierUpdateVoucher } from 'store/forms/actions';

const SupplierEquipmentCosts = ({ disableVoucherTabs, setDisableAction }) => {
  const dispatch = useDispatch();
  const legalVat = useSelector(state => state.forms.carrier.legalVat);
  const voucherInfo = useSelector(state => state.forms?.supplier?.voucherInfo);
  const ean = useSelector(state => state.forms?.supplier?.ean);

  const debouncedUpdateVoucher = useMemo(
    () =>
      debounce((payload, dispatch) => {
        dispatch(supplierUpdateVoucher({ simulation: true, params: payload }));
      }, 1000),
    []
  );

  const initialData = voucherInfo?.voucher || {
    base: { amount: '-', vat: '-', final: '-' },
    additional: { amount: '-', vat: '-', final: '-' },
    nonSubsidized: { amount: '-', vat: '-', final: '-' },
    voucher: { amount: '-', vat: '-', final: '-' },
    beneficiary: { amount: '-', vat: '-', final: '-' },
  };

  const [data, setData] = useState(initialData);

  const parseFormattedNumber = val => {
    if (typeof val === 'number') return val;
    if (!val) return 0;

    const normalized = val
      .toString()
      .replace(/\./g, '') // remove thousands separators
      .replace(',', '.'); // convert comma decimal to dot

    return Number(normalized);
  };

  const formatToCurrencyString = value => {
    if (value === null || value === undefined || value === '-' || value === '')
      return '-';

    const numberValue =
      typeof value === 'string' ? parseFormattedNumber(value) : Number(value);

    if (isNaN(numberValue)) return '-';

    return numberValue.toFixed(2).replace('.', ',');
  };

  const handleChange = (type, key, value) => {
    setData(prevData => {
      const updated = {
        ...prevData,
        [type]: {
          ...prevData[type],
          [key]: value,
        },
      };

      const safeNumber = val => {
        if (val === null || val === undefined || val === '-' || val === '')
          return 0;
        return parseFormattedNumber(val);
      };

      const payload = {
        // otp: '176951',
        voucherCode: voucherInfo?.voucher?.code || '',
        vatId: legalVat,
        ean: ean || '',
        basePrice: safeNumber(
          type === 'base' && key === 'amount' ? value : updated.base.amount
        ),
        baseVat: safeNumber(
          type === 'base' && key === 'vat' ? value : updated.base.vat
        ),
        additionalCosts: safeNumber(
          type === 'additional' && key === 'amount'
            ? value
            : updated.additional.amount
        ),
        additionalCostsVat: safeNumber(
          type === 'additional' && key === 'vat'
            ? value
            : updated.additional.vat
        ),
        nonSubsidizedEquipmentCost: safeNumber(
          type === 'nonSubsidized' && key === 'amount'
            ? value
            : updated.nonSubsidized.amount
        ),
        nonSubsidizedEquipmentCostVat: safeNumber(
          type === 'nonSubsidized' && key === 'vat'
            ? value
            : updated.nonSubsidized.vat
        ),
        action: 'RESERVE',
      };

      debouncedUpdateVoucher(payload, dispatch);
      return updated;
    });
  };

  useEffect(() => {
    const safeFormat = val => {
      return formatToCurrencyString(val);
    };

    setData({
      base: {
        amount: safeFormat(voucherInfo?.voucher?.basePrice),
        vat: safeFormat(voucherInfo?.voucher?.baseVat),
        final: safeFormat(voucherInfo?.voucher?.baseTotal),
      },
      additional: {
        amount: safeFormat(voucherInfo?.voucher?.additionalCosts),
        vat: safeFormat(voucherInfo?.voucher?.additionalCostsVat),
        final: safeFormat(voucherInfo?.voucher?.additionalCostsTotal),
      },
      nonSubsidized: {
        amount: safeFormat(voucherInfo?.voucher?.nonSubsidizedEquipmentCost),
        vat: safeFormat(voucherInfo?.voucher?.nonSubsidizedEquipmentCostVat),
        final: safeFormat(
          voucherInfo?.voucher?.nonSubsidizedEquipmentCostTotal
        ),
      },
      voucher: {
        amount: safeFormat(voucherInfo?.voucher?.voucherValue),
        vat: safeFormat(voucherInfo?.voucher?.voucherValueVat),
        final: safeFormat(voucherInfo?.voucher?.voucherValueTotal),
      },
      beneficiary: {
        amount: safeFormat(voucherInfo?.voucher?.beneficiaryAmount),
        vat: safeFormat(voucherInfo?.voucher?.beneficiaryAmountVat),
        final: safeFormat(voucherInfo?.voucher?.beneficiaryAmountTotal),
      },
    });
  }, [voucherInfo?.voucher]);

  useEffect(() => {
    const isMissing = val =>
      val === undefined ||
      val === null ||
      val === '' ||
      val === '-' ||
      Number(val.replace(',', '.')) === 0;

    // Destructure values from the temporaryData instead of voucherInfo
    const { base, additional, nonSubsidized } = data || {};

    let hasError = false;

    // Rule 1: base amount & vat must always be present
    if (isMissing(base?.amount) || isMissing(base?.vat)) {
      hasError = true;
    }

    // Rule 2: If one of additional fields is filled, both must be present
    const additionalFilled =
      !isMissing(additional?.amount) || !isMissing(additional?.vat);
    if (
      additionalFilled &&
      (isMissing(additional?.amount) || isMissing(additional?.vat))
    ) {
      hasError = true;
    }

    // Rule 3: Same for nonSubsidized fields
    const nonSubsidizedFilled =
      !isMissing(nonSubsidized?.amount) || !isMissing(nonSubsidized?.vat);
    if (
      nonSubsidizedFilled &&
      (isMissing(nonSubsidized?.amount) || isMissing(nonSubsidized?.vat))
    ) {
      hasError = true;
    }

    // Rule 4: Also disable if ean is missing
    if (isMissing(ean)) {
      hasError = true;
    }

    setDisableAction(hasError);
  }, [ean, data]);

  const rows = [
    {
      key: 'base',
      label: 'Κόστος Βασικού Εξοπλισμού',
      editable: !disableVoucherTabs && !isEmpty(ean),
      ...data.base,
    },
    {
      key: 'additional',
      label: 'Κόστος Συμπληρωματικού Εξοπλισμού',
      editable: !disableVoucherTabs && !isEmpty(ean),
      ...data.additional,
    },
    {
      key: 'nonSubsidized',
      label: 'Κόστος Μη Επιδοτούμενου Εξοπλισμού',
      editable: !disableVoucherTabs && !isEmpty(ean),
      ...data.nonSubsidized,
    },
    {
      key: 'voucher',
      label: 'Επιδότηση',
      editable: false,
      ...data.voucher,
    },
    {
      key: 'beneficiary',
      label: 'Πληρωμή Ωφελούμενου',
      editable: false,
      ...data.beneficiary,
    },
  ];

  const columns = [
    {
      title: '',
      dataIndex: 'label',
      key: 'label',
      width: '40%',
    },
    {
      title: 'Ποσό',
      dataIndex: 'amount',
      key: 'amount',
      width: '20%',
      render: (value, record) => {
        if (['voucher', 'beneficiary'].includes(record.key)) {
          return (
            <span className="bold font-size-16">
              {value} {value !== '-' ? '€' : ''}
            </span>
          );
        } else {
          return (
            <FormField
              type="formattedNumber"
              disabled={!record.editable}
              value={value}
              onChange={val => handleChange(record.key, 'amount', val)}
            />
          );
        }
      },
    },
    {
      title: 'Φ.Π.Α.',
      dataIndex: 'vat',
      key: 'vat',
      width: '20%',
      render: (value, record) => {
        if (['voucher', 'beneficiary'].includes(record.key)) {
          return (
            <span className="bold font-size-16">
              {value} {value !== '-' ? '€' : ''}
            </span>
          );
        } else {
          return (
            <FormField
              type="formattedNumber"
              suffixSymbol="percentage" // options "percentage = %" or "currency = €"
              disabled={!record.editable}
              value={value}
              onChange={val => handleChange(record.key, 'vat', val)}
            />
          );
        }
      },
    },
    {
      title: 'Τελικό Ποσό',
      dataIndex: 'final',
      key: 'final',
      width: '20%',
      render: value => (
        <span className="bold font-size-16">
          {value} {value !== '-' ? '€' : ''}
        </span>
      ),
      width: '50%',
    },
  ];

  return (
    <div className="voucher-costs-table">
      <Table
        columns={columns}
        dataSource={rows}
        pagination={false}
        bordered={false}
        rowClassName={() => 'custom-row'}
      />
    </div>
  );
};

export default SupplierEquipmentCosts;
